import {TypedTranslation} from '../../../../i18n';
import {getElementData} from '../../../../incase-domain/elements/base/elementsData';
import {ELEMENT} from '../../../../incase-domain/elements/base/ElementTypes';

export enum PresentationThemeNavItem {
    GENERAL = 'general',
    TEXT = 'text',
    QUESTIONS_SHARED = 'questionsShared',
    QUESTION = 'question',
    YES_NO = 'yesNo',
    MULTI_SELECT = 'multiSelect',
    PHOTO = 'photo',
    SEQUENCE = 'sequence',
    MATCH_PAIR = 'matchPair',
    LIKERT = 'likert',
    IMAGE_MARKUP = 'imageMarkup',
    HTML = 'html',
    IMAGE = 'image',
    BUTTON = 'button',
    TEXT_INPUT = 'textInput',
    FORM = 'form',
    SHAPES_SHARED = 'shapesShared',
    SHAPE = 'shape',
    LINE = 'line',
}

export type ThemeNavGroup = 'general' | 'text' | 'question' | 'content' | 'form' | 'shape';

export type ThemeNavEntry = {
    id: PresentationThemeNavItem;
    labelKey: TypedTranslation;
    icon: string;
    element?: ELEMENT;
    group: ThemeNavGroup;
    shared?: boolean;
};

const QUESTION_GROUP_ICON = 'mynaui:question-hexagon';
const SHAPE_GROUP_ICON = 'solar:stop-linear';

function iconFor(type: ELEMENT, fallback: string): string {
    return getElementData(type)?.icon || fallback;
}

export const THEME_NAV: ThemeNavEntry[] = [
    {
        id: PresentationThemeNavItem.GENERAL,
        labelKey: 'presentationThemesPage.tabs.general',
        icon: 'solar:settings-linear',
        group: 'general',
    },
    {
        id: PresentationThemeNavItem.TEXT,
        labelKey: 'presentationThemesPage.tabs.text',
        icon: iconFor(ELEMENT.STATIC_TEXT, 'solar:text-linear'),
        element: ELEMENT.STATIC_TEXT,
        group: 'text',
    },
    {
        id: PresentationThemeNavItem.QUESTIONS_SHARED,
        labelKey: 'presentationThemesPage.question.types.shared',
        icon: QUESTION_GROUP_ICON,
        element: ELEMENT.QUESTION,
        group: 'question',
        shared: true,
    },
    {
        id: PresentationThemeNavItem.QUESTION,
        labelKey: 'presentationThemesPage.question.types.question',
        icon: iconFor(ELEMENT.QUESTION, QUESTION_GROUP_ICON),
        element: ELEMENT.QUESTION,
        group: 'question',
    },
    {
        id: PresentationThemeNavItem.YES_NO,
        labelKey: 'presentationThemesPage.question.types.yesNo',
        icon: iconFor(ELEMENT.YES_NO_QUESTION_V2, QUESTION_GROUP_ICON),
        element: ELEMENT.YES_NO_QUESTION_V2,
        group: 'question',
    },
    {
        id: PresentationThemeNavItem.MULTI_SELECT,
        labelKey: 'presentationThemesPage.question.types.multiSelect',
        icon: iconFor(ELEMENT.MULTI_SELECT, QUESTION_GROUP_ICON),
        element: ELEMENT.MULTI_SELECT,
        group: 'question',
    },
    {
        id: PresentationThemeNavItem.PHOTO,
        labelKey: 'presentationThemesPage.question.types.photo',
        icon: iconFor(ELEMENT.PHOTO_ANSWER_QUESTION, QUESTION_GROUP_ICON),
        element: ELEMENT.PHOTO_ANSWER_QUESTION,
        group: 'question',
    },
    {
        id: PresentationThemeNavItem.SEQUENCE,
        labelKey: 'presentationThemesPage.question.types.sequence',
        icon: iconFor(ELEMENT.SEQUENCE_QUESTION, QUESTION_GROUP_ICON),
        element: ELEMENT.SEQUENCE_QUESTION,
        group: 'question',
    },
    {
        id: PresentationThemeNavItem.MATCH_PAIR,
        labelKey: 'presentationThemesPage.question.types.matchPair',
        icon: iconFor(ELEMENT.MATCH_PAIR_QUESTION, QUESTION_GROUP_ICON),
        element: ELEMENT.MATCH_PAIR_QUESTION,
        group: 'question',
    },
    {
        id: PresentationThemeNavItem.LIKERT,
        labelKey: 'presentationThemesPage.question.types.likert',
        icon: iconFor(ELEMENT.LIKERT_SCALE, QUESTION_GROUP_ICON),
        element: ELEMENT.LIKERT_SCALE,
        group: 'question',
    },
    {
        id: PresentationThemeNavItem.IMAGE_MARKUP,
        labelKey: 'presentationThemesPage.question.types.imageMarkup',
        icon: iconFor(ELEMENT.IMAGE_MARKUP, QUESTION_GROUP_ICON),
        element: ELEMENT.IMAGE_MARKUP,
        group: 'question',
    },
    {
        id: PresentationThemeNavItem.HTML,
        labelKey: 'presentationThemesPage.content.types.html',
        icon: iconFor(ELEMENT.HTML, 'solar:code-linear'),
        element: ELEMENT.HTML,
        group: 'content',
    },
    {
        id: PresentationThemeNavItem.IMAGE,
        labelKey: 'presentationThemesPage.content.types.image',
        icon: iconFor(ELEMENT.IMAGE, 'solar:gallery-linear'),
        element: ELEMENT.IMAGE,
        group: 'content',
    },
    {
        id: PresentationThemeNavItem.BUTTON,
        labelKey: 'presentationThemesPage.forms.types.button',
        icon: iconFor(ELEMENT.BUTTON, 'solar:cursor-linear'),
        element: ELEMENT.BUTTON,
        group: 'form',
    },
    {
        id: PresentationThemeNavItem.TEXT_INPUT,
        labelKey: 'presentationThemesPage.forms.types.textInput',
        icon: iconFor(ELEMENT.TEXT_INPUT, 'solar:text-field-linear'),
        element: ELEMENT.TEXT_INPUT,
        group: 'form',
    },
    {
        id: PresentationThemeNavItem.FORM,
        labelKey: 'presentationThemesPage.forms.types.form',
        icon: iconFor(ELEMENT.FORM, 'solar:clipboard-list-linear'),
        element: ELEMENT.FORM,
        group: 'form',
    },
    {
        id: PresentationThemeNavItem.SHAPES_SHARED,
        labelKey: 'presentationThemesPage.shapes.types.shared',
        icon: SHAPE_GROUP_ICON,
        element: ELEMENT.RECTANGLE,
        group: 'shape',
        shared: true,
    },
    {
        id: PresentationThemeNavItem.SHAPE,
        labelKey: 'presentationThemesPage.shapes.types.shape',
        icon: iconFor(ELEMENT.RECTANGLE, SHAPE_GROUP_ICON),
        element: ELEMENT.RECTANGLE,
        group: 'shape',
    },
    {
        id: PresentationThemeNavItem.LINE,
        labelKey: 'presentationThemesPage.shapes.types.line',
        icon: iconFor(ELEMENT.LINE_V2, 'ph:line-segment-duotone'),
        element: ELEMENT.LINE_V2,
        group: 'shape',
    },
];

export function getThemeNavEntry(id: PresentationThemeNavItem): ThemeNavEntry {
    return THEME_NAV.find((entry) => entry.id === id) ?? THEME_NAV[0];
}
